/**
 * Discover collections (sections/discover-collections.liquid)
 *
 * <discover-collections-stack>  Mobile 3D card stack. Swipe (touch) or drag (mouse)
 *                               50px+ to move one card; wraps around both ends.
 * <discover-collections-slider> Desktop full-width slider. Infinite loop using a
 *                               cloned first/last slide, dots, optional autoplay.
 *
 * Behaviour and timings match the live site. Native browser APIs only; safe to load twice.
 */
(() => {
  const SWIPE_THRESHOLD = 50;
  const TRANSITION_LOCK_MS = 100;
  const VISIBLE_RANGE = 2;

  const prefersReducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  if (!customElements.get('discover-collections-stack')) {
    class DiscoverCollectionsStack extends HTMLElement {
      /** @type {HTMLElement[]} */
      #cards = [];
      #index = 0;
      #locked = false;
      #startX = 0;
      #startY = 0;
      #mouseDown = false;
      #swiped = false;
      #controller = new AbortController();

      connectedCallback() {
        this.#cards = Array.from(this.querySelectorAll('[data-stack-card]'));
        if (this.#cards.length < 2) return;

        const viewport = this.querySelector('[data-stack-viewport]') ?? this;
        const { signal } = this.#controller;

        viewport.addEventListener('touchstart', this.#onTouchStart.bind(this), { passive: true, signal });
        viewport.addEventListener('touchmove', this.#onTouchMove.bind(this), { passive: false, signal });
        viewport.addEventListener('touchend', this.#onTouchEnd.bind(this), { signal });
        viewport.addEventListener('mousedown', this.#onMouseDown.bind(this), { signal });
        window.addEventListener('mouseup', this.#onMouseUp.bind(this), { signal });
        viewport.addEventListener('click', this.#onClick.bind(this), { capture: true, signal });
        viewport.addEventListener('dragstart', (event) => event.preventDefault(), { signal });
        this.addEventListener('keydown', this.#onKeydown.bind(this), { signal });

        this.#render();
      }

      disconnectedCallback() {
        this.#controller.abort();
        this.#controller = new AbortController();
      }

      next() {
        this.#go(1);
      }

      previous() {
        this.#go(-1);
      }

      /** @param {number} step */
      #go(step) {
        if (this.#locked) return;
        this.#locked = true;
        const total = this.#cards.length;
        this.#index = (this.#index + step + total) % total;
        this.#render();
        setTimeout(() => {
          this.#locked = false;
        }, TRANSITION_LOCK_MS);
      }

      /** Positions each card relative to the front card; CSS maps data-position to transforms. */
      #render() {
        const total = this.#cards.length;

        for (const [i, card] of this.#cards.entries()) {
          let offset = i - this.#index;
          if (offset > total / 2) offset -= total;
          if (offset < -total / 2) offset += total;

          const visible = Math.abs(offset) <= VISIBLE_RANGE;
          card.dataset.position = visible ? String(offset) : 'hidden';
          card.inert = !visible;
          card.toggleAttribute('aria-current', offset === 0);
        }
      }

      /** @param {number} endX */
      #handleSwipe(endX) {
        const diff = this.#startX - endX;
        if (Math.abs(diff) < SWIPE_THRESHOLD) return;
        this.#swiped = true;
        if (diff > 0) {
          this.next();
          return;
        }
        this.previous();
      }

      /** @param {TouchEvent} event */
      #onTouchStart(event) {
        if (this.#locked) return;
        this.#startX = event.touches[0].clientX;
        this.#startY = event.touches[0].clientY;
      }

      /** Block page scroll only when the gesture is mostly horizontal. @param {TouchEvent} event */
      #onTouchMove(event) {
        const diffX = Math.abs(event.touches[0].clientX - this.#startX);
        const diffY = Math.abs(event.touches[0].clientY - this.#startY);
        if (diffX > diffY && event.cancelable) event.preventDefault();
      }

      /** @param {TouchEvent} event */
      #onTouchEnd(event) {
        if (this.#locked) return;
        this.#handleSwipe(event.changedTouches[0].clientX);
      }

      /** @param {MouseEvent} event */
      #onMouseDown(event) {
        if (this.#locked || event.button !== 0) return;
        this.#mouseDown = true;
        this.#swiped = false;
        this.#startX = event.clientX;
      }

      /** @param {MouseEvent} event */
      #onMouseUp(event) {
        if (!this.#mouseDown || this.#locked) return;
        this.#mouseDown = false;
        this.#handleSwipe(event.clientX);
      }

      /** Don't open the collection at the end of a swipe or drag. @param {MouseEvent} event */
      #onClick(event) {
        if (!this.#swiped) return;
        event.preventDefault();
        event.stopPropagation();
        this.#swiped = false;
      }

      /** @param {KeyboardEvent} event */
      #onKeydown(event) {
        const isRtl = getComputedStyle(this).direction === 'rtl';
        if (event.key === 'ArrowRight') {
          event.preventDefault();
          isRtl ? this.previous() : this.next();
        } else if (event.key === 'ArrowLeft') {
          event.preventDefault();
          isRtl ? this.next() : this.previous();
        }
      }
    }

    customElements.define('discover-collections-stack', DiscoverCollectionsStack);
  }

  if (!customElements.get('discover-collections-slider')) {
    class DiscoverCollectionsSlider extends HTMLElement {
      /** @type {HTMLElement | null} */
      #track = null;
      /** @type {HTMLButtonElement[]} */
      #dots = [];
      #index = 1;
      #realCount = 0;
      #slideCount = 0;
      #timer = 0;
      #hovered = false;
      #focused = false;
      #controller = new AbortController();
      /** @type {ResizeObserver | null} */
      #resizeObserver = null;

      connectedCallback() {
        this.#track = this.querySelector('[data-slider-track]');
        if (!this.#track) return;

        const slides = Array.from(this.#track.querySelectorAll('[data-slider-slide]'));
        this.#dots = Array.from(this.querySelectorAll('[data-slider-dot]'));
        this.#realCount = slides.length;
        if (this.#realCount < 2) return;

        // Clones at both ends give the seamless infinite loop.
        const firstClone = this.#makeClone(slides[0]);
        const lastClone = this.#makeClone(slides[slides.length - 1]);
        this.#track.append(firstClone);
        this.#track.prepend(lastClone);
        this.#slideCount = this.#realCount + 2;

        this.#index = 1;
        this.#track.scrollLeft = this.#track.clientWidth;

        const { signal } = this.#controller;
        this.#track.addEventListener('scroll', this.#onScroll.bind(this), { passive: true, signal });
        for (const [i, dot] of this.#dots.entries()) {
          dot.addEventListener('click', () => this.goTo(i + 1), { signal });
        }
        this.addEventListener('mouseenter', () => { this.#hovered = true; }, { signal });
        this.addEventListener('mouseleave', () => { this.#hovered = false; }, { signal });
        this.addEventListener('focusin', () => { this.#focused = true; }, { signal });
        this.addEventListener('focusout', () => { this.#focused = this.contains(document.activeElement); }, { signal });

        this.#resizeObserver = new ResizeObserver(() => {
          this.#track.scrollLeft = this.#index * this.#track.clientWidth;
        });
        this.#resizeObserver.observe(this.#track);

        this.#updateDots();
        this.#startAutoplay();
      }

      disconnectedCallback() {
        this.#controller.abort();
        this.#controller = new AbortController();
        this.#resizeObserver?.disconnect();
        clearInterval(this.#timer);
      }

      /**
       * Smooth-scroll to a track position (1 = first real slide).
       * @param {number} index
       */
      goTo(index) {
        this.#index = index;
        this.#track.scrollTo({
          left: this.#index * this.#track.clientWidth,
          behavior: prefersReducedMotion() ? 'auto' : 'smooth'
        });
        this.#updateDots();
      }

      /** @param {HTMLElement} slide */
      #makeClone(slide) {
        const clone = /** @type {HTMLElement} */ (slide.cloneNode(true));
        clone.setAttribute('aria-hidden', 'true');
        clone.inert = true;
        clone.removeAttribute('data-slider-slide');
        for (const element of clone.querySelectorAll('[id]')) element.removeAttribute('id');
        return clone;
      }

      /** Jump from a clone to its real slide without animation (same as the live site). */
      #onScroll() {
        const width = this.#track.clientWidth;
        if (!width) return;
        this.#index = Math.round(this.#track.scrollLeft / width);

        if (this.#index === 0) {
          this.#track.scrollLeft = this.#realCount * width;
          this.#index = this.#realCount;
        }

        if (this.#index === this.#slideCount - 1) {
          this.#track.scrollLeft = width;
          this.#index = 1;
        }

        this.#updateDots();
      }

      #updateDots() {
        const active = (((this.#index - 1) % this.#realCount) + this.#realCount) % this.#realCount;
        for (const [i, dot] of this.#dots.entries()) {
          const isActive = i === active;
          dot.classList.toggle('is-active', isActive);
          if (isActive) {
            dot.setAttribute('aria-current', 'true');
          } else {
            dot.removeAttribute('aria-current');
          }
        }
      }

      #startAutoplay() {
        if (this.dataset.autoplay !== 'true' || prefersReducedMotion()) return;
        const speed = Number(this.dataset.autoplaySpeed) || 8000;

        this.#timer = setInterval(() => {
          if (this.#hovered || this.#focused || document.hidden) return;
          this.goTo(this.#index + 1);
        }, speed);
      }
    }

    customElements.define('discover-collections-slider', DiscoverCollectionsSlider);
  }
})();
